import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";

const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Why Us", href: "#why-us" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNav = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: href } });
      return;
    }
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const goHome = () => {
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <nav
      className="sticky top-0 z-50 border-b border-[color:var(--gs-line)] backdrop-blur supports-[backdrop-filter]:bg-[rgba(10,14,26,0.6)] bg-[rgba(10,14,26,0.85)]"
      data-testid="main-navbar"
    >
      <div className="mx-auto grid max-w-[var(--container-max)] grid-cols-3 items-center px-4 py-2.5 sm:px-6">
        {/* Left links (desktop) / hamburger (mobile) */}
        <div className="flex items-center justify-start">
          <div className="hidden items-center gap-6 lg:flex">
            {NAV_LINKS.slice(0, 2).map((link) => (
              <button
                key={link.href}
                onClick={() => handleNav(link.href)}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-white/70 hover:text-[color:var(--gs-gold-400)]"
              >
                {link.label}
              </button>
            ))}
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-button"
                className="text-white hover:bg-white/10 hover:text-white lg:hidden"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-72 border-white/10 bg-[color:var(--gs-navy-850)] text-white"
            >
              <div className="mt-8 flex flex-col gap-1">
                <img src="/logo-small.png" alt="GrowthSync Solutions logo" className="mb-4 h-16 w-16 rounded-lg" />
                {NAV_LINKS.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => handleNav(link.href)}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="rounded-lg px-3 py-2.5 text-left text-base text-white/80 hover:bg-white/5 hover:text-[color:var(--gs-gold-400)]"
                  >
                    {link.label}
                  </button>
                ))}
                <Button
                  onClick={() => handleNav("#contact")}
                  data-testid="mobile-nav-quote-button"
                  className="mt-4 bg-[color:var(--gs-gold-400)] font-semibold text-[color:var(--gs-navy-950)] hover:bg-[color:var(--gs-gold-500)]"
                >
                  Request a Quote
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Center logo - top middle as requested */}
        <div className="flex items-center justify-center">
          <button
            onClick={goHome}
            aria-label="GrowthSync Solutions - home"
            data-testid="navbar-logo"
            className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--gs-gold-400)]"
          >
            <img
              src="/logo-small.png"
              alt="GrowthSync Solutions logo"
              width="52"
              height="52"
              className="h-12 w-12 rounded-lg sm:h-[52px] sm:w-[52px]"
            />
          </button>
        </div>

        {/* Right links + CTA (desktop) */}
        <div className="flex items-center justify-end gap-6">
          <div className="hidden items-center gap-6 lg:flex">
            {NAV_LINKS.slice(2).map((link) => (
              <button
                key={link.href}
                onClick={() => handleNav(link.href)}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-white/70 hover:text-[color:var(--gs-gold-400)]"
              >
                {link.label}
              </button>
            ))}
          </div>
          <Button
            onClick={() => handleNav("#contact")}
            size="sm"
            data-testid="nav-quote-button"
            className="bg-[color:var(--gs-gold-400)] font-semibold text-[color:var(--gs-navy-950)] hover:bg-[color:var(--gs-gold-500)] focus-visible:ring-2 focus-visible:ring-[color:var(--gs-gold-400)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--gs-navy-900)]"
          >
            <span className="hidden sm:inline">Request a Quote</span>
            <span className="sm:hidden">Quote</span>
          </Button>
        </div>
      </div>
    </nav>
  );
};
